import { useTranslation } from "react-i18next";

import CartesianIcon from "@/components/Dataset/Icons/CartesianIcon";
import FileIcon from "@/components/Dataset/Icons/FileIcon";
import FormIcon from "@/components/Dataset/Icons/FormIcon";
import GpsIcon from "@/components/Dataset/Icons/GpsIcon";
import ImageIcon from "@/components/Dataset/Icons/ImageIcon";
import PlanPositioningIcon from "@/components/Dataset/Icons/PlanPositioningIcon";
import RecommendationIcon from "@/components/Dataset/Icons/RecommendationIcon";
import { TAB_PARAM_KEYS } from "@/constants/TabKeys";
import { CardContextTabs } from "@/ui/card-context-tabs";

import { useGetInterventionTemplateSection } from "./InterventionTemplateSection.query";

// TODO IMP-1214 fix complexity
/* eslint-disable complexity */
const InterventionTemplateSection = ({
  interventionTemplateId,
  disableOnEmpty,
  formRender,
  cartesianRender,
  planPositioningRender,
  gpsRender,
  imageRender,
  fileRender,
  recommendationRender,
  validationRender,
  cardProps,
  parentLoading = false,
  hasRecommendation,
}) => {
  const { t } = useTranslation();

  const { section, isLoading } = useGetInterventionTemplateSection(
    interventionTemplateId
  );

  const loading = isLoading || cardProps?.loading || parentLoading;
  const actions = validationRender ? [validationRender()] : [];

  function hasAnyTemplateOfTypes(...props) {
    const args = Array.prototype.slice.call(props);
    return section?.templates?.some((template) =>
      args?.includes(template.type)
    );
  }

  const hasForm = hasAnyTemplateOfTypes("form");
  const hasCartesian = hasAnyTemplateOfTypes("cartesian");
  const hasPlanPositioning = hasAnyTemplateOfTypes("planPositioning");
  const hasGps = hasAnyTemplateOfTypes("gps");
  const hasImage = hasAnyTemplateOfTypes("image");
  const hasFile = hasAnyTemplateOfTypes("file");

  function getTemplatesByType(...props) {
    const args = Array.prototype.slice.call(props);
    return section?.templates?.filter((template) => {
      return args.includes(template.type);
    });
  }

  const datasetTabs = [
    {
      key: "form",
      label: t("global:datasetTypes.form"),
      icon: <FormIcon filled={hasForm} />,
      disabled: disableOnEmpty && !hasForm && !isLoading,
      render: () => formRender(getTemplatesByType("form")),
    },
    {
      key: "cartesian",
      label: t("global:datasetTypes.cartesian"),
      icon: <CartesianIcon filled={hasCartesian} />,
      disabled: disableOnEmpty && !hasCartesian && !isLoading,
      render: () => cartesianRender(getTemplatesByType("cartesian")),
    },
    {
      key: "gps",
      label: t("global:datasetTypes.gps"),
      icon: <GpsIcon filled={hasGps} />,
      disabled: disableOnEmpty && !hasGps && !isLoading,
      render: () => gpsRender(getTemplatesByType("gps")),
    },
    {
      key: "file",
      label: t("global:datasetTypes.file"),
      icon: <FileIcon filled={hasFile} />,
      disabled: disableOnEmpty && !hasFile && !isLoading,
      render: () => fileRender(getTemplatesByType("file")),
    },
    {
      key: "plan_positioning",
      label: t("global:datasetTypes.planPositioning"),
      icon: <PlanPositioningIcon filled={hasPlanPositioning} />,
      disabled: disableOnEmpty && !hasPlanPositioning && !isLoading,
      render: () =>
        planPositioningRender(getTemplatesByType("planPositioning")),
    },
  ];

  if (recommendationRender) {
    datasetTabs.push({
      key: "recommendation",
      label: t("global:recommendation"),
      icon: <RecommendationIcon filled={hasRecommendation} />,
      render: () => recommendationRender(),
    });
  }

  if (imageRender) {
    datasetTabs.push({
      key: "image",
      label: t("global:datasetTypes.image"),
      icon: <ImageIcon filled={hasImage} />,
      disabled: disableOnEmpty && !hasImage && !isLoading,
      render: () => imageRender(getTemplatesByType("image")),
    });
  }

  const defaultActiveKey =
    datasetTabs.find((tab) => !tab.disabled)?.key || "form";

  return (
    <CardContextTabs
      title={section?.name ?? ""}
      description={section?.description ?? ""}
      tabs={datasetTabs}
      defaultActiveKey={defaultActiveKey}
      tabKey={TAB_PARAM_KEYS.CONTEXT_DATASET_TYPE_KEY}
      tabProps={{ size: "medium" }}
      actions={actions}
      {...cardProps}
      loading={loading}
      isScrollable
    />
  );
};

export default InterventionTemplateSection;
